import React, { useState, useCallback } from 'react';
import { Annotation } from '@/services/annotationService';
import { useAnnotations, useMeasurementOverride } from '@/hooks/useAnnotations';
import { useFloorPlanScale } from '@/hooks/useFloorPlanScale';
import { getDimensionLabelSizing } from './dimensionLabelSizing';

interface CircleAnnotationProps {
  annotation: Annotation;
  isSelected: boolean;
  isHovered: boolean;
  scale: number;
  onClick: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  isInteractive: boolean;
  floorPlanId?: string;
}

export function CircleAnnotation({
  annotation,
  isSelected,
  isHovered,
  scale,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onTouchStart,
  onTouchEnd,
  onTouchMove,
  isInteractive,
  floorPlanId,
}: CircleAnnotationProps) {
  const [isResizing, setIsResizing] = useState(false);
  const [resizePreviewRadius, setResizePreviewRadius] = useState<number | null>(null);
  const { updateAnnotation } = useAnnotations(annotation.floor_plan_id);
  const measurementOverride = useMeasurementOverride();
  const scaleId = floorPlanId || annotation.floor_plan_id || '';
  const { formatMeasurement: fpFormatMeasurement, hasScale: fpHasScale } =
    useFloorPlanScale(scaleId);
  const formatMeasurement = measurementOverride?.formatMeasurement ?? fpFormatMeasurement;
  const hasScale = measurementOverride?.hasScale ?? fpHasScale;
  const dimensionLabel = getDimensionLabelSizing(scale);

  const { data, style } = annotation;
  const cx = data.cx!;
  const cy = data.cy!;
  const radius = resizePreviewRadius ?? data.radius!;

  // Calculate the stroke width - annotations scale with zoom (no division by scale)
  const strokeWidth = style.strokeWidth || 1;

  // Handle resize start
  const handleResizeStart = useCallback(
    (e: React.MouseEvent, _direction: string) => {
      if (!isInteractive) return;
      e.stopPropagation();
      e.preventDefault();
      setIsResizing(true);

      // Get the SVG element once
      const svg =
        (e.target as SVGElement).ownerSVGElement || (e.target as SVGElement).closest('svg');
      if (!svg) return;

      // Get initial mouse position in SVG coordinates
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const initialMousePos = pt.matrixTransform(svg.getScreenCTM()!.inverse());

      // Calculate initial distance from center
      const centerX = annotation.data.cx!;
      const centerY = annotation.data.cy!;
      const initialDx = initialMousePos.x - centerX;
      const initialDy = initialMousePos.y - centerY;
      const initialDistance = Math.sqrt(initialDx * initialDx + initialDy * initialDy);

      // Store the original radius from annotation data
      const originalRadius = annotation.data.radius!;
      let lastRadius = originalRadius;

      const handleMove = (clientX: number, clientY: number) => {
        const pt = svg.createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        const currentMousePos = pt.matrixTransform(svg.getScreenCTM()!.inverse());

        // Calculate current distance from center
        const dx = currentMousePos.x - centerX;
        const dy = currentMousePos.y - centerY;
        const currentDistance = Math.sqrt(dx * dx + dy * dy);

        // Calculate radius delta based on distance change
        const distanceDelta = currentDistance - initialDistance;
        const newRadius = Math.max(10, originalRadius + distanceDelta);

        // Store the last radius
        lastRadius = newRadius;

        setResizePreviewRadius(newRadius);
      };

      let completed = false;
      const captureOptions: AddEventListenerOptions = { capture: true };
      const touchMoveOptions: AddEventListenerOptions = { capture: true, passive: false };

      const handleMouseMove = (e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        handleMove(e.clientX, e.clientY);
      };

      const handleTouchMove = (e: TouchEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (e.touches.length === 1) {
          handleMove(e.touches[0].clientX, e.touches[0].clientY);
        }
      };

      const cleanup = () => {
        document.removeEventListener('mousemove', handleMouseMove, captureOptions);
        document.removeEventListener('mouseup', handleEnd, captureOptions);
        window.removeEventListener('mouseup', handleEnd, captureOptions);
        document.removeEventListener('pointerup', handleEnd, captureOptions);
        window.removeEventListener('pointerup', handleEnd, captureOptions);
        document.removeEventListener('pointercancel', handleCancel, captureOptions);
        window.removeEventListener('pointercancel', handleCancel, captureOptions);
        document.removeEventListener('touchmove', handleTouchMove, touchMoveOptions);
        document.removeEventListener('touchend', handleEnd, captureOptions);
        document.removeEventListener('touchcancel', handleCancel, captureOptions);
        window.removeEventListener('keydown', handleKeyDown, captureOptions);
        window.removeEventListener('blur', handleCancel);
      };

      const finishResize = (commit: boolean) => {
        if (completed) return;
        completed = true;
        setIsResizing(false);
        cleanup();

        if (!commit) {
          setResizePreviewRadius(null);
          return;
        }

        // Final update with history if radius changed
        if (lastRadius !== originalRadius) {
          void updateAnnotation({
            id: annotation.id,
            data: {
              data: {
                ...annotation.data,
                radius: lastRadius,
              },
            },
            skipHistory: false, // Create history entry for the final position
          }).finally(() => setResizePreviewRadius(null));
        } else {
          setResizePreviewRadius(null);
        }
      };

      function handleEnd() {
        finishResize(true);
      }

      function handleCancel() {
        finishResize(false);
      }

      function handleKeyDown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
          event.preventDefault();
          finishResize(false);
        }
      }

      document.addEventListener('mousemove', handleMouseMove, captureOptions);
      document.addEventListener('mouseup', handleEnd, captureOptions);
      window.addEventListener('mouseup', handleEnd, captureOptions);
      document.addEventListener('pointerup', handleEnd, captureOptions);
      window.addEventListener('pointerup', handleEnd, captureOptions);
      document.addEventListener('pointercancel', handleCancel, captureOptions);
      window.addEventListener('pointercancel', handleCancel, captureOptions);
      document.addEventListener('touchmove', handleTouchMove, touchMoveOptions);
      document.addEventListener('touchend', handleEnd, captureOptions);
      document.addEventListener('touchcancel', handleCancel, captureOptions);
      window.addEventListener('keydown', handleKeyDown, captureOptions);
      window.addEventListener('blur', handleCancel);
    },
    [annotation, updateAnnotation, isInteractive],
  );

  return (
    <g data-annotation-id={annotation.id} className={isInteractive ? 'cursor-pointer' : ''}>
      {/* Invisible hit area for easier selection - only for stroked circles */}
      {(!style.fill || style.fill === 'none' || style.fill === 'transparent') && (
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="transparent"
          strokeWidth={Math.max(16 / scale, (strokeWidth * 8) / scale)}
          pointerEvents={isInteractive ? 'auto' : 'none'}
          style={{ cursor: isInteractive ? 'pointer' : 'default' }}
          onClick={onClick}
          onMouseDown={isResizing ? undefined : onMouseDown}
          onTouchStart={isResizing ? undefined : onTouchStart}
          onTouchEnd={isResizing ? undefined : onTouchEnd}
          onTouchMove={isResizing ? undefined : onTouchMove}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
      )}

      {/* Main circle */}
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill={style.fill || 'none'}
        fillOpacity={style.fill ? style.opacity || 1 : 0}
        stroke={style.color || '#000000'}
        strokeWidth={strokeWidth}
        strokeOpacity={style.opacity || 1}
        strokeDasharray={style.strokeDasharray}
        pointerEvents={
          style.fill && style.fill !== 'none' && style.fill !== 'transparent' && isInteractive
            ? 'auto'
            : 'none'
        }
        onClick={onClick}
        onMouseDown={isResizing ? undefined : onMouseDown}
        onTouchStart={isResizing ? undefined : onTouchStart}
        onTouchEnd={isResizing ? undefined : onTouchEnd}
        onTouchMove={isResizing ? undefined : onTouchMove}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />

      {/* Hover effect - clean dashed outline */}
      {isHovered && !isSelected && (
        <circle
          cx={cx}
          cy={cy}
          r={radius + 3 / scale}
          fill="none"
          stroke="#6366f1"
          strokeWidth={1.5 / scale}
          strokeOpacity={0.7}
          strokeDasharray={`${4 / scale} ${2 / scale}`}
          pointerEvents="none"
        />
      )}

      {/* Selection/hover indicator */}
      {(isSelected || isHovered) && (
        <>
          {/* Resize handles when selected */}
          {isSelected && isInteractive && (
            <>
              {[
                { cx: cx, cy: cy - radius, dir: 'n' },
                { cx: cx + radius, cy: cy, dir: 'e' },
                { cx: cx, cy: cy + radius, dir: 's' },
                { cx: cx - radius, cy: cy, dir: 'w' },
              ].map((handle, index) => (
                <g key={index}>
                  {/* Invisible larger hit area for touch */}
                  <rect
                    x={handle.cx - 12 / scale}
                    y={handle.cy - 12 / scale}
                    width={24 / scale}
                    height={24 / scale}
                    fill="transparent"
                    pointerEvents="all"
                    style={{ cursor: 'ew-resize' }}
                    onMouseDown={e => handleResizeStart(e, handle.dir)}
                    onTouchStart={e => {
                      if (e.touches.length === 1) {
                        e.preventDefault();
                        e.stopPropagation();
                        const syntheticEvent = {
                          ...e,
                          clientX: e.touches[0].clientX,
                          clientY: e.touches[0].clientY,
                          preventDefault: () => e.preventDefault(),
                          stopPropagation: () => e.stopPropagation(),
                          target: e.target,
                          currentTarget: e.currentTarget,
                        } as unknown as React.MouseEvent;
                        handleResizeStart(syntheticEvent, handle.dir);
                      }
                    }}
                  />
                  {/* Visible handle */}
                  <circle
                    cx={handle.cx}
                    cy={handle.cy}
                    r={6 / scale}
                    fill="#2563eb"
                    stroke="white"
                    strokeWidth={1 / scale}
                    pointerEvents="none"
                  />
                </g>
              ))}
            </>
          )}

          {/* Selection outline */}
          {isSelected && (
            <circle
              cx={cx}
              cy={cy}
              r={radius + 2 / scale}
              fill="none"
              stroke="#2563eb"
              strokeWidth={2 / scale}
              strokeOpacity={0.5}
              strokeDasharray={`${4 / scale} ${2 / scale}`}
              pointerEvents="none"
            />
          )}
        </>
      )}

      {/* Diameter label when showDimensions is enabled */}
      {data.showDimensions && hasScale && (
        <text
          x={cx}
          y={cy + radius + dimensionLabel.offset}
          fill="#3b82f6"
          fontSize={dimensionLabel.fontSize}
          fontWeight="600"
          textAnchor="middle"
          dominantBaseline="hanging"
          pointerEvents="none"
          style={{ userSelect: 'none' }}
        >
          {'\u2300'} {formatMeasurement(radius * 2)}
        </text>
      )}
    </g>
  );
}
